import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Card, Typography } from '@mui/material';
import ListData from '../table-elements/list-data.component';
import SnackbarInfo from '../common/snackbar-info.component';
import { getChannelAssignRulesetVideos } from '../../services/ruleset.service';
import { SCHEDULED_VIDEOS_LIST } from '../../config/module-configs/content-videos.config';
import useAbortableRequest from '../../hooks/abortable-request.hook';

const ListScheduledVideos = ({
  title,
  channelContentScheduleInfoId,
  channelId,
  activeScheduleType,
}) => {
  const { getSignal, isAbortError } = useAbortableRequest();
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [videosList, setVideosList] = useState({
    page: 0,
    rowsPerPage: 100,
    totalRows: 0,
    rows: [],
    columns: SCHEDULED_VIDEOS_LIST,
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
    paginationAction: 'total_videos',
  });

  const handlePageChange = (v) => {
    setVideosList({
      ...videosList,
      page: v,
      reloadCounter: videosList.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRowsPerPageChange = (v) => {
    setVideosList({
      ...videosList,
      rowsPerPage: v,
      page: 0,
      reloadCounter: videosList.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSortingChange = (sortBy, sortOrder) => {
    setVideosList({
      ...videosList,
      sortBy,
      sortOrder,
      reloadCounter: videosList.reloadCounter + 1,
      loading: true,
    });
  };
  const handlePaginationActionChange = (v) => {
    setVideosList({
      ...videosList,
      paginationAction: v,
      reloadCounter: videosList.reloadCounter + 1,
      loading: true,
    });
  };

  useEffect(() => {
    const signal = getSignal();
    const params = [];
    params.push(`page=${videosList.page + 1}`);
    params.push(`perPage=${videosList.rowsPerPage}`);
    if (videosList.sortBy && videosList.sortOrder) {
      params.push(`sortBy=${videosList.sortBy}`);
      params.push(`sortOrder=${videosList.sortOrder}`);
    }
    if (videosList.paginationAction) {
      params.push(`filters=${videosList.paginationAction}`);
    }
    params.push(`type=${activeScheduleType}`);

    const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
    getChannelAssignRulesetVideos(
      channelContentScheduleInfoId,
      channelId,
      paramsQuery,
      signal
    )
      .then((res) => {
        if (!signal.aborted) {
          setVideosList({
            ...videosList,
            loading: false,
            totalRows: res.data.totalRows || 0,
            rows: res.data.rows || [],
            error: false,
          });
        }
      })
      .catch((err) => {
        if (isAbortError(err)) {
          return;
        }
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
      });
  }, [videosList.reloadCounter]);

  return (
    <Box marginY={2} height="100%">
      <Typography variant="h6" display="flex" alignItems="center">
        {title}
      </Typography>

      <Card
        sx={{
          maxHeight: '85%',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <ListData
          columns={videosList.columns}
          rows={videosList.rows}
          page={videosList.page}
          rowsPerPage={videosList.rowsPerPage}
          totalRows={videosList.totalRows}
          loading={videosList.loading}
          actions={[]}
          error={videosList.error}
          sortBy={videosList.sortBy}
          sortOrder={videosList.sortOrder}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSortChange={handleSortingChange}
          paginationAction={videosList.paginationAction}
          onPaginationActionChange={handlePaginationActionChange}
        />
      </Card>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Box>
  );
};

ListScheduledVideos.propTypes = {
  title: PropTypes.string.isRequired,
  channelContentScheduleInfoId: PropTypes.number.isRequired,
  channelId: PropTypes.number.isRequired,
  activeScheduleType: PropTypes.string.isRequired,
};

export default ListScheduledVideos;
